import React, { useState, useEffect, useMemo } from "react"
import { useSearchParams, useNavigate, Link } from "react-router-dom"
import { searchSimulationData } from "@/apis/simulator-api"
import Header from "@/routes/_layout/header"
import BackButton from "@/components/buttons/back-button"
import useIsMobile from "@/hooks/use-is-mobile"

const FILTER_OPTIONS = {
  pipe_gauge: ["15", "20", "25", "32", "40", "50", "75", "100"],
  pipe_type: ["가정용", "일반용", "대중탕용", "공업용"],
  leak_type: ["A", "B", "C", "D"],
  leak_severity: ["심각", "주의", "미세"]
}

const SimulatorResultPage = () => {
  const isMobile = useIsMobile()
  const mode = isMobile ? "mobile" : "desktop"
  const HeaderComponent = Header.default[mode]

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [filters, setFilters] = useState({
    pipe_gauge: [],
    pipe_type: [],
    leak_type: [],
    leak_severity: [],
    leak_rate: 0,
    leak_mode: "gt"
  })

  useEffect(() => {
    const newFilters = {
      pipe_gauge: searchParams.getAll("pipe_gauge"),
      pipe_type: searchParams.getAll("pipe_type"),
      leak_type: searchParams.getAll("leak_type"),
      leak_severity: searchParams.getAll("leak_severity"),
      leak_rate: searchParams.get("leak_rate") || 0,
      leak_mode: searchParams.get("leak_mode") || "gt"
    }
    setFilters(newFilters)
    fetchData(newFilters)
  }, [searchParams])

  const fetchData = async (filterParams) => {
    setLoading(true)
    try {
      const response = await searchSimulationData(filterParams)
      setData(response.results || [])
      setCurrentPage(1)
    } catch (error) {
      console.error("Failed to fetch data", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCheckboxChange = (category, value) => {
    setFilters((prev) => {
      const currentList = prev[category]
      if (currentList.includes(value)) {
        return { ...prev, [category]: currentList.filter((item) => item !== value) }
      } else {
        return { ...prev, [category]: [...currentList, value] }
      }
    })
  }

  const handleApplyFilters = () => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v))
      } else {
        params.append(key, value)
      }
    })
    navigate(`/simulator/result?${params.toString()}`)
  }

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = useMemo(
    () => data.slice(indexOfFirstItem, indexOfLastItem),
    [data, indexOfFirstItem, indexOfLastItem]
  )
  const totalPages = Math.ceil(data.length / itemsPerPage)

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber)

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col h-screen overflow-hidden">
      <HeaderComponent />

      <div className="flex flex-1 overflow-hidden max-w-400 w-full mx-auto">
        {/* === 사이드바 (필터 수정) === */}
        <aside className="w-80 bg-white border-r border-slate-200 overflow-y-auto p-6 hidden lg:flex flex-col shadow-sm z-10">
          <div className="mb-6 flex items-center justify-between">
            <div className="mb-2">
              <BackButton className="-ml-2 px-2" onClick={() => navigate("/select")} />
            </div>
            <h2 className="font-bold text-slate-800">조건 수정</h2>
          </div>

          <div className="space-y-6 flex-1">
            {/* 구경 */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">구경 (mm)</label>
              <div className="flex flex-wrap gap-2">
                {FILTER_OPTIONS.pipe_gauge.map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.pipe_gauge.includes(opt) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={filters.pipe_gauge.includes(opt)}
                      onChange={() => handleCheckboxChange("pipe_gauge", opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            {/* 업종 */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">업종</label>
              <div className="flex flex-wrap gap-2">
                {FILTER_OPTIONS.pipe_type.map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.pipe_type.includes(opt) ? "bg-green-600 text-white border-green-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={filters.pipe_type.includes(opt)}
                      onChange={() => handleCheckboxChange("pipe_type", opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            {/* 누수 유형 & 심각도 */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">유형 / 심각도</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {FILTER_OPTIONS.leak_type.map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.leak_type.includes(opt) ? "bg-indigo-500 text-white border-indigo-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={filters.leak_type.includes(opt)}
                      onChange={() => handleCheckboxChange("leak_type", opt)}
                    />
                    {opt}형
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {FILTER_OPTIONS.leak_severity.map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.leak_severity.includes(opt) ? "bg-red-500 text-white border-red-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={filters.leak_severity.includes(opt)}
                      onChange={() => handleCheckboxChange("leak_severity", opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            {/* 누수율 */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">누수율 (%)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  className="w-20 p-2 text-sm border rounded-lg"
                  value={filters.leak_rate}
                  onChange={(e) => setFilters({ ...filters, leak_rate: e.target.value })}
                />
                <div className="flex gap-2 text-sm">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      checked={filters.leak_mode === "gt"}
                      onChange={() => setFilters({ ...filters, leak_mode: "gt" })}
                    />{" "}
                    이상
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      checked={filters.leak_mode === "lt"}
                      onChange={() => setFilters({ ...filters, leak_mode: "lt" })}
                    />{" "}
                    이하
                  </label>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleApplyFilters}
            className="w-full mt-6 bg-slate-800 text-white py-3 rounded-xl font-bold hover:bg-black transition-colors"
          >
            조건 적용하기
          </button>
        </aside>

        {/* === 메인 결과 영역 === */}
        <main className="flex-1 flex flex-col min-w-0 bg-slate-50">
          {/* 헤더 */}
          <header className="px-8 py-6 bg-white border-b border-slate-200 flex justify-between items-center shadow-sm">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">검색 결과</h1>
              <p className="text-slate-500 text-sm mt-1">
                총 <strong className="text-blue-600">{data.length.toLocaleString()}</strong>건의
                데이터가 검색되었습니다.
              </p>
            </div>

            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
                setCurrentPage(1)
              }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="10">10개씩 보기</option>
              <option value="20">20개씩 보기</option>
              <option value="50">50개씩 보기</option>
              <option value="100">100개씩 보기</option>
            </select>
          </header>

          {/* 테이블 영역 */}
          <div className="flex-1 overflow-auto p-8">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : currentItems.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50 text-slate-600 font-bold text-sm uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4">No</th>
                      <th className="px-6 py-4">구경</th>
                      <th className="px-6 py-4">업종</th>
                      <th className="px-6 py-4 text-center">누수유형</th>
                      <th className="px-6 py-4 text-center">심각도</th>
                      <th className="px-6 py-4 text-right">누수율 (%)</th>
                      <th className="px-6 py-4 text-right">누수량</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                    {currentItems.map((item, index) => (
                      <tr key={index} className="hover:bg-blue-50/50 transition-colors">
                        <td className="px-6 py-4 text-slate-400 font-mono">
                          {indexOfFirstItem + index + 1}
                        </td>
                        <td className="px-6 py-4 font-bold">{item["구경"]}mm</td>
                        <td className="px-6 py-4">{item["업종"]}</td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-bold
                            ${
                              item["누수유형"] === "A"
                                ? "bg-blue-100 text-blue-700"
                                : item["누수유형"] === "B"
                                  ? "bg-green-100 text-green-700"
                                  : item["누수유형"] === "C"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {item["누수유형"]}형
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-bold
                            ${
                              item["누수심각도"] === "심각"
                                ? "bg-red-100 text-red-700"
                                : item["누수심각도"] === "주의"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {item["누수심각도"]}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-900">
                          {item["누수율"]}%
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-500">
                          {item["누수량"] || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400 bg-white rounded-2xl border border-dashed border-slate-300">
                <span className="text-4xl mb-2">📭</span>
                <p>검색 결과가 없습니다.</p>
              </div>
            )}

            {/* 페이지네이션 */}
            {data.length > 0 && (
              <div className="flex justify-center mt-8 pb-8 gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border bg-white disabled:opacity-50 hover:bg-slate-50"
                >
                  이전
                </button>
                <span className="px-4 py-2 font-bold text-slate-700">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border bg-white disabled:opacity-50 hover:bg-slate-50"
                >
                  다음
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default SimulatorResultPage
